import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { requestJson } from "../../../../helpers/api";
import MonchisAutoConciliacionFilters from "./MonchisAutoConciliacionFilters";
import MonchisAutoConciliacionTabs from "./MonchisAutoConciliacionTabs";
import {
  CoincidenciaAutomatica,
  CoincidenciaAutomaticaRespuesta,
  CoincidenciaPendiente,
  ConciliacionAutomaticaAplicarResponse,
  RegistroPendienteConciliacion,
  ResumenFila,
  SucursalSistemaOption,
  abreviarIdentificador,
  agruparPorSucursal,
  construirFechaIso,
  construirNombreCajero,
  describirOrigenConciliacion,
  enriquecerCoincidencia,
  enriquecerCoincidencias,
  formatCurrencyGs,
  formatFechaHoraTexto,
  formatearFechaResumen,
  normalizarFechaClave,
  normalizarPendiente,
} from "./MonchisAutoConciliacion.helpers";


const MonchisAutoConciliacion = () => {
  const [autoModalVisible, setAutoModalVisible] = useState(false);
  const [autoFechaDesde, setAutoFechaDesde] = useState(() => construirFechaIso(-3));
  const [autoFechaHasta, setAutoFechaHasta] = useState(() => construirFechaIso(0));
  const [autoResultados, setAutoResultados] = useState<CoincidenciaAutomatica[]>([]);
  const [autoCoincidenciasBase, setAutoCoincidenciasBase] = useState<CoincidenciaAutomatica[]>([]);
  const [autoSugeridosDuplicados, setAutoSugeridosDuplicados] = useState<CoincidenciaAutomatica[]>([]);
  const [autoCargando, setAutoCargando] = useState(false);
  const [autoError, setAutoError] = useState<string | null>(null);
  const [autoTab, setAutoTab] = useState<"summary" | "found" | "suggested" | "missing">("summary");
  const [autoMissingTab, setAutoMissingTab] = useState<"sistema" | "monchis">("sistema");
  const [autoPendientesSistema, setAutoPendientesSistema] = useState<RegistroPendienteConciliacion[]>([]);
  const [autoPendientesMonchis, setAutoPendientesMonchis] = useState<RegistroPendienteConciliacion[]>([]);
  const [autoBaseSistema, setAutoBaseSistema] = useState<RegistroPendienteConciliacion[]>([]);
  const [autoBaseMonchis, setAutoBaseMonchis] = useState<RegistroPendienteConciliacion[]>([]);
  const [autoConciliando, setAutoConciliando] = useState(false);
  const [autoConciliarMensaje, setAutoConciliarMensaje] = useState<string | null>(null);
  const [autoBuscar, setAutoBuscar] = useState("");
  const [autoBasesCargando, setAutoBasesCargando] = useState(false);
  const [autoSucursalFiltro, setAutoSucursalFiltro] = useState("");
  const [autoSucursalesSistema, setAutoSucursalesSistema] = useState<SucursalSistemaOption[]>([]);
  const [autoRangoFechas, setAutoRangoFechas] = useState<Date[]>([]);
  const [autoTotalSistema, setAutoTotalSistema] = useState(0);
  const [autoTotalMonchis, setAutoTotalMonchis] = useState(0);
  const [autoExportando, setAutoExportando] = useState(false);
  const [autoConfirmarReconciliacion, setAutoConfirmarReconciliacion] = useState(false);
  const [autoReconcilacionConteo, setAutoReconcilacionConteo] = useState({
    total: 0,
    pendientes: 0,
  });
  const [manualConciliandoId, setManualConciliandoId] = useState<string | null>(null);
  const [manualConciliadas, setManualConciliadas] = useState<Set<string>>(() => new Set());
  const puedeRehacerConciliacionManual = true;
  const [autoActualizandoEstado, setAutoActualizandoEstado] = useState(false);
  const [manualRehacerPendiente, setManualRehacerPendiente] = useState<CoincidenciaAutomatica | null>(null);
  const controlesDeshabilitados = autoConciliando;
  const AUTO_TAB_PAGE_SIZE = 25;
  const [autoFoundPage, setAutoFoundPage] = useState(0);
  const [autoSuggestedPage, setAutoSuggestedPage] = useState(0);
  const [autoMissingPages, setAutoMissingPages] = useState({ sistema: 0, monchis: 0 });

  const sucursalFiltroSeleccionada = useMemo(
    () => autoSucursalesSistema.find((item) => String(item.idsucursal) === autoSucursalFiltro) ?? null,
    [autoSucursalFiltro, autoSucursalesSistema],
  );

  const sucursalMonchisSet = useMemo(() => {
    if (!sucursalFiltroSeleccionada) return null;
    const nombres = (sucursalFiltroSeleccionada.monchisSucursales ?? []).map((nombre) =>
      nombre.toLowerCase(),
    );
    return new Set(nombres);
  }, [sucursalFiltroSeleccionada]);

  const coincideSucursalFiltro = (
    idsucursal?: number | null,
    sistemaNombre?: string | null,
    monchisNombre?: string | null,
  ) => {
    if (!sucursalFiltroSeleccionada) return true;
    if (idsucursal && idsucursal === sucursalFiltroSeleccionada.idsucursal) {
      return true;
    }
    const nombreSistema = sucursalFiltroSeleccionada.sucursalNombre
      ? sucursalFiltroSeleccionada.sucursalNombre.toLowerCase()
      : null;
    if (sistemaNombre && nombreSistema && sistemaNombre.toLowerCase() === nombreSistema) {
      return true;
    }
    if (monchisNombre && sucursalMonchisSet && sucursalMonchisSet.size > 0) {
      return sucursalMonchisSet.has(monchisNombre.toLowerCase());
    }
    return false;
  };

  const extraerNumeroCampo = (valor: unknown): number | null => {
    if (typeof valor === "number" && Number.isFinite(valor)) {
      return valor;
    }
    if (typeof valor === "string") {
      const numero = Number(valor);
      return Number.isFinite(numero) ? numero : null;
    }
    return null;
  };

  const extraerTextoCampo = (valor: unknown): string | null => {
    if (typeof valor === "string") {
      const texto = valor.trim();
      return texto.length > 0 ? texto : null;
    }
    return null;
  };

  const obtenerSucursalSistemaDesdeMonchis = (fuente: Record<string, unknown>) => {
    const idsucursal =
      extraerNumeroCampo(fuente["idsucursal_sistema"]) ??
      extraerNumeroCampo(fuente["idsucursalSistema"]) ??
      extraerNumeroCampo(fuente["sistema_idsucursal"]) ??
      extraerNumeroCampo(fuente["sistemaIdsucursal"]) ??
      null;
    const nombreSistema =
      extraerTextoCampo(fuente["sucursal_nombre_sistema"]) ??
      extraerTextoCampo(fuente["sucursalNombreSistema"]) ??
      extraerTextoCampo(fuente["sistema_sucursal_nombre"]) ??
      extraerTextoCampo(fuente["sistemaSucursalNombre"]) ??
      null;
    return { idsucursal, nombreSistema };
  };

  const esConciliacionActiva = useCallback(
    (fila: CoincidenciaAutomatica) =>
      Boolean(fila.conciliacionId) && fila.conciliacionEstado === "conciliado",
    [],
  );

  const aplicarFiltrosCoincidencias = useCallback(
    (lista: CoincidenciaAutomatica[]) => {
      const filtrados = lista.filter((item) =>
        coincideSucursalFiltro(item.registroIdsucursal ?? null, item.registroSucursalNombre, item.movimientoSucursal),
      );
      const termino = autoBuscar.trim().toLowerCase();
      if (!termino) {
        return filtrados;
      }
      const normalizarTexto = (valor?: string | null) => valor?.toString().toLowerCase() ?? "";
      return filtrados.filter((item) => {
        const campos = [
          item.registroId,
          item.registroNumeroMovimiento,
          item.registroSucursalNombre,
          formatFechaHoraTexto(item.registroFechaDia),
          formatFechaHoraTexto(item.registroFechaHoraAperturaTurno),
          formatFechaHoraTexto(item.registroFechaCierre),
          formatFechaHoraTexto(item.registroFechaApertura),
          item.registroTurno,
          item.movimientoId,
          item.movimientoNroOrden,
          item.movimientoNroTransaccion,
          item.movimientoSucursal,
          item.movimientoTipoPago,
          formatFechaHoraTexto(item.movimientoFechaHora),
          item.movimientoEstado,
        ];
        return campos.some((campo) => normalizarTexto(campo).includes(termino));
      });
    },
    [autoBuscar, coincideSucursalFiltro],
  );

  const autoCoincidenciasBaseFiltradas = useMemo(
    () => aplicarFiltrosCoincidencias(autoCoincidenciasBase),
    [aplicarFiltrosCoincidencias, autoCoincidenciasBase],
  );

  const autoSugeridosFiltrados = useMemo(
    () => aplicarFiltrosCoincidencias(autoSugeridosDuplicados),
    [aplicarFiltrosCoincidencias, autoSugeridosDuplicados],
  );
  const autoCoincidenciasPendientes = useMemo(
    () => autoCoincidenciasBase.filter((item) => !esConciliacionActiva(item)),
    [autoCoincidenciasBase, esConciliacionActiva],
  );
  const autoCoincidenciasPendientesFiltradas = useMemo(
    () => aplicarFiltrosCoincidencias(autoCoincidenciasPendientes),
    [aplicarFiltrosCoincidencias, autoCoincidenciasPendientes],
  );
  const autoCoincidenciasPendientesSet = useMemo(() => {
    const conjunto = new Set<string>();
    autoResultados.forEach((item) => {
      if (item.registroId && item.movimientoId && !esConciliacionActiva(item)) {
        conjunto.add(`${item.registroId}-${item.movimientoId}`);
      }
    });
    return conjunto;
  }, [autoResultados, esConciliacionActiva]);

  const autoPendientesSistemaFiltrados = useMemo(
    () =>
      autoPendientesSistema.filter((item) =>
        coincideSucursalFiltro(item.registroIdsucursal ?? null, item.sucursal ?? null, item.movimientoSucursal ?? null),
      ),
    [autoPendientesSistema, sucursalFiltroSeleccionada, sucursalMonchisSet],
  );

  const autoPendientesMonchisFiltrados = useMemo(
    () =>
      autoPendientesMonchis.filter((item) =>
        (() => {
          const datosSistema = obtenerSucursalSistemaDesdeMonchis(item as Record<string, unknown>);
          return coincideSucursalFiltro(
            datosSistema.idsucursal ?? item.registroIdsucursal ?? null,
            datosSistema.nombreSistema ?? item.sucursal ?? null,
            item.sucursal ?? item.movimientoSucursal ?? null,
          );
        })(),
      ),
    [autoPendientesMonchis, sucursalFiltroSeleccionada, sucursalMonchisSet],
  );

  const autoBaseSistemaFiltrados = useMemo(
    () =>
      autoBaseSistema.filter((item) =>
        coincideSucursalFiltro(item.registroIdsucursal ?? null, item.sucursal ?? null, item.movimientoSucursal ?? null),
      ),
    [autoBaseSistema, sucursalFiltroSeleccionada, sucursalMonchisSet],
  );

  const autoBaseMonchisFiltrados = useMemo(
    () =>
      autoBaseMonchis.filter((item) =>
        (() => {
          const datosSistema = obtenerSucursalSistemaDesdeMonchis(item as Record<string, unknown>);
          return coincideSucursalFiltro(
            datosSistema.idsucursal ?? item.registroIdsucursal ?? null,
            datosSistema.nombreSistema ?? item.sucursal ?? null,
            item.sucursal ?? item.movimientoSucursal ?? null,
          );
        })(),
      ),
    [autoBaseMonchis, sucursalFiltroSeleccionada, sucursalMonchisSet],
  );

  const usaFiltroSucursal = Boolean(sucursalFiltroSeleccionada);
  const totalSistemaVisible = usaFiltroSucursal ? autoBaseSistemaFiltrados.length : autoTotalSistema;
  const totalMonchisVisible = usaFiltroSucursal ? autoBaseMonchisFiltrados.length : autoTotalMonchis;
  const pendientesSistemaVisibles = usaFiltroSucursal ? autoPendientesSistemaFiltrados.length : autoPendientesSistema.length;
  const pendientesMonchisVisibles = usaFiltroSucursal ? autoPendientesMonchisFiltrados.length : autoPendientesMonchis.length;
  const sugeridosDuplicadosVisibles = autoSugeridosFiltrados.length;
  const puedeGuardar = autoCoincidenciasBaseFiltradas.length > 0;

  const totalSinCoincidencias = useMemo(
    () => autoPendientesSistemaFiltrados.length + autoPendientesMonchisFiltrados.length,
    [autoPendientesSistemaFiltrados, autoPendientesMonchisFiltrados],
  );

  const autoResumen = useMemo(() => {
    const mapa = new Map<string, ResumenFila>();

    const obtenerEntrada = (fecha?: string | null) => {
      const fechaClave = normalizarFechaClave(fecha) ?? null;
      const clave = `${fechaClave ?? "sin-fecha"}`;
      if (!mapa.has(clave)) {
        mapa.set(clave, {
          clave,
          fechaClave,
          fechaTexto: formatearFechaResumen(fechaClave),
          sistema: {
            conciliados: { tickets: 0, monto: 0 },
            pendientes: { tickets: 0, monto: 0 },
          },
          monchis: {
            conciliados: { tickets: 0, monto: 0 },
            pendientes: { tickets: 0, monto: 0 },
          },
          sugeridos: { tickets: 0, monto: 0 },
        });
      }
      return mapa.get(clave)!;
    };

    autoCoincidenciasBaseFiltradas.forEach((item) => {
      const fecha = item.registroFechaDia ?? normalizarFechaClave(item.movimientoFechaHora);
      const entrada = obtenerEntrada(fecha);
      const montoSistema = Number(item.registroMonto ?? 0);
      const montoMonchis = Number(item.movimientoImporteConDelivery ?? item.movimientoImporteOrden ?? 0);
      entrada.sistema.conciliados.tickets += 1;
      entrada.sistema.conciliados.monto += montoSistema;
      entrada.monchis.conciliados.tickets += 1;
      entrada.monchis.conciliados.monto += montoMonchis;
    });

    autoPendientesSistemaFiltrados.forEach((item) => {
      const fecha = normalizarFechaClave(item.fecha);
      const entrada = obtenerEntrada(fecha);
      const monto = Number(item.monto ?? 0);
      entrada.sistema.pendientes.tickets += 1;
      entrada.sistema.pendientes.monto += monto;
    });

    autoPendientesMonchisFiltrados.forEach((item) => {
      const fecha = normalizarFechaClave(item.fecha ?? item.movimientoFechaHora);
      const entrada = obtenerEntrada(fecha);
      const monto = Number(item.monto ?? item.movimientoImporteConDelivery ?? item.movimientoImporteOrden ?? 0);
      entrada.monchis.pendientes.tickets += 1;
      entrada.monchis.pendientes.monto += monto;
    });

    autoSugeridosFiltrados.forEach((item) => {
      const fecha = item.registroFechaDia ?? normalizarFechaClave(item.movimientoFechaHora);
      const entrada = obtenerEntrada(fecha);
      const monto = Number(
        item.movimientoImporteConDelivery ?? item.movimientoImporteOrden ?? item.registroMonto ?? 0,
      );
      entrada.sugeridos.tickets += 1;
      entrada.sugeridos.monto += monto;
    });

    return Array.from(mapa.values()).sort((a, b) => {
      if (a.fechaClave && b.fechaClave) {
        return a.fechaClave.localeCompare(b.fechaClave);
      }
      if (a.fechaClave) return -1;
      if (b.fechaClave) return 1;
      return 0;
    });
  }, [autoCoincidenciasBaseFiltradas, autoPendientesSistemaFiltrados, autoPendientesMonchisFiltrados]);

  const cargarSucursalesSistema = useCallback(async () => {
    try {
      const respuesta = await requestJson<{
        ok: boolean;
        sucursales?: SucursalSistemaOption[];
      }>("/api/tesoreria/importaciones/monchis/conciliaciones/sucursales-sistema");
      setAutoSucursalesSistema(respuesta.sucursales ?? []);
    } catch {
      // silencio
    }
  }, []);

  const cargarBasesConciliacion = useCallback(async (opciones?: { evitarCargando?: boolean }) => {
    if (!autoFechaDesde || !autoFechaHasta) {
      setAutoBaseSistema([]);
      setAutoBaseMonchis([]);
      return;
    }
    if (!opciones?.evitarCargando) {
      setAutoBasesCargando(true);
    }
    try {
      const params = new URLSearchParams({
        fechaDesde: autoFechaDesde,
        fechaHasta: autoFechaHasta,
      });
      if (autoSucursalFiltro) {
        params.set("idsucursal", autoSucursalFiltro);
      }
      const [sistema, monchis] = await Promise.all([
        requestJson<{
          ok: boolean;
          total?: number;
          registros?: RegistroPendienteConciliacion[];
        }>(`/api/tesoreria/importaciones/monchis/conciliaciones/registros-base?${params.toString()}`),
        requestJson<{
          ok: boolean;
          total?: number;
          registros?: RegistroPendienteConciliacion[];
        }>(`/api/tesoreria/importaciones/monchis/conciliaciones/movimientos-base?${params.toString()}`),
      ]);
      setAutoBaseSistema(sistema.registros ?? []);
      setAutoBaseMonchis(monchis.registros ?? []);
      setAutoTotalSistema(sistema.total ?? sistema.registros?.length ?? 0);
      setAutoTotalMonchis(monchis.total ?? monchis.registros?.length ?? 0);
      const detalle = await requestJson<{
        registros?: Record<string, unknown>[];
        movimientos?: Record<string, unknown>[];
        coincidencias?: CoincidenciaAutomatica[];
        sugeridos?: CoincidenciaAutomatica[];
      }>(`/api/tesoreria/importaciones/monchis/conciliaciones/bases-detalle?${params.toString()}`);
      const coincidenciasDet = Array.isArray(detalle.coincidencias) ? detalle.coincidencias : [];
      const sugeridosDet = Array.isArray(detalle.sugeridos) ? detalle.sugeridos : [];
      setAutoCoincidenciasBase(enriquecerCoincidencias(coincidenciasDet));
      setAutoSugeridosDuplicados(enriquecerCoincidencias(sugeridosDet));
      if (Array.isArray(detalle.registros)) {
        setAutoTotalSistema(detalle.registros.length);
      }
      if (Array.isArray(detalle.movimientos)) {
        setAutoTotalMonchis(detalle.movimientos.length);
      }
    } catch {
      setAutoBaseSistema([]);
      setAutoBaseMonchis([]);
    } finally {
      if (!opciones?.evitarCargando) {
        setAutoBasesCargando(false);
      }
    }
  }, [autoFechaDesde, autoFechaHasta, autoSucursalFiltro]);

  useEffect(() => {
    if (autoModalVisible) {
      void cargarSucursalesSistema();
    }
  }, [autoModalVisible, cargarSucursalesSistema]);

  useEffect(() => {
    if (!autoModalVisible) return;
    const fechas: Date[] = [];
    if (autoFechaDesde) {
      const fecha = new Date(`${autoFechaDesde}T00:00:00`);
      if (!Number.isNaN(fecha.getTime())) {
        fechas.push(fecha);
      }
    }
    if (autoFechaHasta) {
      const fecha = new Date(`${autoFechaHasta}T00:00:00`);
      if (!Number.isNaN(fecha.getTime())) {
        if (!fechas.length || fecha.getTime() !== fechas[0].getTime()) {
          fechas.push(fecha);
        }
      }
    }
    setAutoRangoFechas(fechas);
  }, [autoModalVisible, autoFechaDesde, autoFechaHasta]);

  const manejarCambioRangoFechas = useCallback(
    (dates: Date[]) => {
      setAutoRangoFechas(dates);
      if (!dates.length) {
        setAutoFechaDesde("");
        setAutoFechaHasta("");
        return;
      }
      const [desde, hasta] = dates;
      if (desde) {
        setAutoFechaDesde(desde.toISOString().slice(0, 10));
      }
      if (dates.length > 1 && hasta) {
        setAutoFechaHasta(hasta.toISOString().slice(0, 10));
      } else if (desde) {
        setAutoFechaHasta(desde.toISOString().slice(0, 10));
      }
    },
    [],
  );

  const conciliarCoincidenciasAutomaticas = async (
    seleccionadas?: CoincidenciaAutomatica[],
  ) => {
    const tabla = seleccionadas?.length ? seleccionadas : autoResultados;
    if (!tabla.length) {
      setAutoError("No hay coincidencias para conciliar.");
      return;
    }
    setAutoConciliando(true);
    setAutoActualizandoEstado(true);
    setAutoError(null);
    try {
      const CHUNK_SIZE = 200;
      const acumulado: ConciliacionAutomaticaAplicarResponse & { conflictos?: string[] } = {
        ok: true,
        total: 0,
        guardadas: 0,
        nuevas: 0,
        actualizadas: 0,
        omitidas: 0,
        conflictos: [],
      };
      for (let i = 0; i < tabla.length; i += CHUNK_SIZE) {
        const lote = tabla.slice(i, i + CHUNK_SIZE);
        const payload = {
          coincidencias: lote.map((item) => ({
            registroId: item.registroId,
            movimientoId: item.movimientoId,
          })),
        };
        const respuesta = await requestJson<ConciliacionAutomaticaAplicarResponse>(
          `/api/tesoreria/importaciones/monchis/conciliaciones/automaticas`,
          {
            method: "POST",
            body: JSON.stringify(payload),
          },
        );
        acumulado.total += respuesta.total;
        acumulado.guardadas += respuesta.guardadas;
        acumulado.nuevas += respuesta.nuevas;
        acumulado.actualizadas += respuesta.actualizadas;
        acumulado.omitidas += respuesta.omitidas;
        if (respuesta.conflictos?.length) {
          acumulado.conflictos = [...(acumulado.conflictos ?? []), ...respuesta.conflictos];
        }
      }
      const conflictosTexto =
        acumulado.conflictos && acumulado.conflictos.length > 0
          ? ` Conflictos: ${acumulado.conflictos.join(" · ")}.`
          : "";
      const mensaje = `Registramos ${acumulado.guardadas} conciliaciones${
        autoCoincidenciasBase.length ? ` de ${autoCoincidenciasBase.length} coincidencias identificadas` : ""
      } (nuevas ${acumulado.nuevas}, actualizadas ${acumulado.actualizadas}).${conflictosTexto}`;
      await buscarConciliacionAutomatica({ preservarMensajes: true, evitarCargando: true });
      setAutoConciliarMensaje(mensaje);
    } catch (err) {
      const mensaje =
        err instanceof Error ? err.message : "No se pudo conciliar las coincidencias seleccionadas.";
      setAutoError(mensaje);
    } finally {
      setAutoConciliando(false);
      setAutoActualizandoEstado(false);
    }
  };

  const toggleAutoModal = () => {
    setAutoModalVisible((prev) => !prev);
    if (!autoModalVisible) {
      setAutoResultados([]);
      setAutoError(null);
      setAutoTab("summary");
      setAutoMissingTab("sistema");
      setAutoPendientesSistema([]);
      setAutoPendientesMonchis([]);
      setAutoConciliarMensaje(null);
      setAutoConciliando(false);
      setAutoTotalSistema(0);
      setAutoTotalMonchis(0);
      setAutoBaseSistema([]);
      setAutoBaseMonchis([]);
      setAutoCoincidenciasBase([]);
      setAutoSugeridosDuplicados([]);
      setAutoBasesCargando(false);
      setAutoBuscar("");
      setManualConciliadas(new Set());
    }
  };

  const conciliarCoincidenciaManual = async (fila: CoincidenciaAutomatica) => {
    const clave = `${fila.registroId}-${fila.movimientoId}`;
    setManualConciliandoId(clave);
    setAutoActualizandoEstado(true);
    setAutoError(null);
    try {
      const payload = {
        coincidencias: [{ registroId: fila.registroId, movimientoId: fila.movimientoId }],
      };
      const respuesta = await requestJson<ConciliacionAutomaticaAplicarResponse>(
        `/api/tesoreria/importaciones/monchis/conciliaciones/manual`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      const conflictosTexto =
        respuesta.conflictos && respuesta.conflictos.length > 0
          ? ` Conflictos: ${respuesta.conflictos.join(" · ")}.`
          : "";
      const mensaje = `Registramos ${respuesta.guardadas} conciliación manual.${conflictosTexto}`;
      await buscarConciliacionAutomatica({ preservarMensajes: true, evitarCargando: true });
      setAutoConciliarMensaje(mensaje);
      setManualConciliadas((prev) => {
        const actualizado = new Set(prev);
        actualizado.add(clave);
        return actualizado;
      });
    } catch (error) {
      const mensaje =
        error instanceof Error ? error.message : "No se pudo conciliar manualmente la coincidencia seleccionada.";
      setAutoError(mensaje);
    } finally {
      setManualConciliandoId(null);
      setAutoActualizandoEstado(false);
    }
  };

  const solicitarRehacerConciliacionManual = (fila: CoincidenciaAutomatica) => {
    setManualRehacerPendiente(fila);
  };

  const cancelarRehacerConciliacionManual = () => {
    setManualRehacerPendiente(null);
  };

  const confirmarRehacerConciliacionManual = async () => {
    if (!manualRehacerPendiente) return;
    await liberarConciliacionManual(manualRehacerPendiente);
  };

  const liberarConciliacionManual = async (fila: CoincidenciaAutomatica) => {
    const clave = `${fila.registroId}-${fila.movimientoId}`;
    setManualConciliandoId(clave);
    setAutoActualizandoEstado(true);
    setAutoError(null);
    try {
      const payload = {
        coincidencias: [{ registroId: fila.registroId, movimientoId: fila.movimientoId }],
      };
      const respuesta = await requestJson<{ reabiertas?: number }>(
        `/api/tesoreria/importaciones/monchis/conciliaciones/manual/revertir`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      const totalLiberadas = respuesta.reabiertas ?? 0;
      const mensaje =
        totalLiberadas > 0
          ? `Liberamos ${totalLiberadas} coincidencia para que puedas conciliarla nuevamente.`
          : "No encontramos una conciliación activa para liberar.";
      await buscarConciliacionAutomatica({ preservarMensajes: true, evitarCargando: true });
      setAutoConciliarMensaje(mensaje);
      setManualConciliadas((prev) => {
        const actualizado = new Set(prev);
        actualizado.delete(clave);
        return actualizado;
      });
      setManualRehacerPendiente(null);
    } catch (error) {
      const mensaje =
        error instanceof Error ? error.message : "No pudimos liberar la conciliación seleccionada.";
      setAutoError(mensaje);
    } finally {
      setManualConciliandoId(null);
      setAutoActualizandoEstado(false);
    }
  };

  const iniciarConciliacion = () => {
    if (autoCoincidenciasBaseFiltradas.length === 0) {
      setAutoError("No hay coincidencias detectadas en el rango seleccionado.");
      return;
    }
    const total = autoCoincidenciasBaseFiltradas.length;
    const pendientes = autoCoincidenciasPendientesFiltradas.length;
    if (total > pendientes) {
      setAutoReconcilacionConteo({ total, pendientes });
      setAutoConfirmarReconciliacion(true);
      return;
    }
    void conciliarCoincidenciasAutomaticas(autoCoincidenciasBaseFiltradas);
  };

  const buscarConciliacionAutomatica = async (opciones?: { preservarMensajes?: boolean; evitarCargando?: boolean }) => {
    if (!autoFechaDesde || !autoFechaHasta) {
      setAutoError("Seleccioná el rango a analizar.");
      return;
    }
    if (!opciones?.evitarCargando) {
      setAutoCargando(true);
    }
    setAutoError(null);
    if (!opciones?.preservarMensajes) {
      setAutoConciliarMensaje(null);
    }
    try {
      const params = new URLSearchParams({
        fechaDesde: autoFechaDesde,
        fechaHasta: autoFechaHasta,
      });
      const respuesta = await requestJson<CoincidenciaAutomaticaRespuesta>(
        `/api/tesoreria/importaciones/monchis/conciliaciones/automaticas?${params.toString()}`,
      );
      const registros = enriquecerCoincidencias(respuesta.registros ?? []);
      setAutoResultados(registros);
      const sinSistema = Array.isArray(respuesta.sinCoincidenciasMonchis) ? respuesta.sinCoincidenciasMonchis : [];
      const sinMonchis = Array.isArray(respuesta.sinCoincidenciasPedidosYa)
        ? respuesta.sinCoincidenciasPedidosYa
        : [];
      setAutoPendientesSistema(sinSistema.map((item, index) => normalizarPendiente(item, index, "sistema")));
      setAutoPendientesMonchis(sinMonchis.map((item, index) => normalizarPendiente(item, index, "monchis")));
      setAutoTotalSistema(respuesta.totalRegistradosSistema ?? sinSistema.length + registros.length);
      setAutoTotalMonchis(respuesta.totalRegistradosMonchis ?? sinMonchis.length + registros.length);
      void cargarBasesConciliacion({ evitarCargando: opciones?.evitarCargando });
    } catch (err) {
      setAutoPendientesSistema([]);
      setAutoPendientesMonchis([]);
      const mensaje =
        err instanceof Error
          ? err.message
          : "No se pudo consultar la conciliación automática. Intentá nuevamente.";
      setAutoError(mensaje);
    } finally {
      if (!opciones?.evitarCargando) {
        setAutoCargando(false);
      }
    }
  };

  const descargarBasesExcel = async () => {
    if (!autoFechaDesde || !autoFechaHasta) {
      setAutoError("Seleccioná el rango a exportar antes de generar el archivo.");
      return;
    }
    setAutoExportando(true);
    try {
      const params = new URLSearchParams({
        fechaDesde: autoFechaDesde,
        fechaHasta: autoFechaHasta,
      });
      if (autoSucursalFiltro) {
        params.set("idsucursal", autoSucursalFiltro);
      }
      const respuesta = await requestJson<{
        registros?: Record<string, unknown>[];
        movimientos?: Record<string, unknown>[];
        coincidencias?: CoincidenciaAutomatica[];
        sugeridos?: CoincidenciaAutomatica[];
        pendientesSistema?: Record<string, unknown>[];
        pendientesMonchis?: Record<string, unknown>[];
      }>(`/api/tesoreria/importaciones/monchis/conciliaciones/bases-detalle?${params.toString()}`);
      const registros = Array.isArray(respuesta.registros) ? respuesta.registros : [];
      const movimientos = Array.isArray(respuesta.movimientos) ? respuesta.movimientos : [];
      const coincidenciasServidor = Array.isArray(respuesta.coincidencias)
        ? (respuesta.coincidencias as CoincidenciaAutomatica[])
        : [];
      const duplicadosServidor = Array.isArray(respuesta.sugeridos)
        ? (respuesta.sugeridos as CoincidenciaAutomatica[])
        : [];

      const normalizarMontoClave = (valor: unknown) => {
        if (valor === null || valor === undefined) {
          return null;
        }
        const numero = Number(valor);
        if (!Number.isFinite(numero)) {
          return null;
        }
        return numero.toFixed(2);
      };
      const construirClave = (fecha: string | null, idsucursal: number | null, monto: string | null) =>
        fecha && idsucursal !== null && monto ? `${fecha}|${idsucursal}|${monto}` : null;
      const ajustarFechaLocal = (valor?: unknown) => {
        if (typeof valor !== "string") {
          return null;
        }
        const fecha = new Date(valor);
        if (Number.isNaN(fecha.getTime())) {
          return null;
        }
        const local = new Date(fecha.getTime() - fecha.getTimezoneOffset() * 60000);
        return local.toISOString().slice(0, 10);
      };

      type Normalizado = {
        clave: string | null;
        exportRow: Record<string, unknown>;
        original: Record<string, unknown>;
      };

      const extraerFechaTexto = (valor: unknown) => {
        if (typeof valor === "string") {
          return valor.slice(0, 10);
        }
        if (valor instanceof Date) {
          const local = new Date(valor.getTime() - valor.getTimezoneOffset() * 60000);
          return local.toISOString().slice(0, 10);
        }
        return null;
      };

      const registrosNormalizados: Normalizado[] = registros.map((fila, index) => {
        const idsucursal =
          typeof fila.idsucursal === "number"
            ? fila.idsucursal
            : typeof fila.idsucursal === "string"
              ? Number(fila.idsucursal)
              : null;
        const fechaDia =
          extraerFechaTexto(fila.fecha_dia) ??
          extraerFechaTexto((fila as Record<string, unknown>).fecha);
        const monto = normalizarMontoClave(fila.monto);
        const clave = construirClave(fechaDia, idsucursal, monto);
        return {
          clave,
          original: fila,
          exportRow: {
            orden: index + 1,
            ...fila,
            fecha_dia_local: fechaDia ?? "",
            clave_conciliacion: clave ?? "",
          },
        };
      });

      const movimientosNormalizados: (Normalizado & { idsucursalSistema: number | null })[] = movimientos.map(
        (fila, index) => {
          const idsucursalSistema =
            typeof fila.idsucursal_sistema === "number"
              ? fila.idsucursal_sistema
              : typeof fila.idsucursal_sistema === "string"
                ? Number(fila.idsucursal_sistema)
                : null;
          const fechaLocal = ajustarFechaLocal(fila.fecha_hora);
          const monto = normalizarMontoClave(
            fila.importe_total_con_delivery !== undefined && fila.importe_total_con_delivery !== null
              ? fila.importe_total_con_delivery
              : fila.importe_total_orden,
          );
          const clave = construirClave(fechaLocal, idsucursalSistema, monto);
          return {
            clave,
            idsucursalSistema,
            original: fila,
            exportRow: {
              orden: index + 1,
              ...fila,
              fecha_dia_local: fechaLocal ?? "",
              clave_conciliacion: clave ?? "",
            },
          };
        },
      );

      const obtenerIdTexto = (valor: unknown) => {
        if (typeof valor === "string") {
          return valor;
        }
        if (typeof valor === "number") {
          return valor.toString();
        }
        return null;
      };
      const extraerIdDeFila = (fila: Record<string, unknown>) =>
        obtenerIdTexto(fila["id"] as string | number | undefined);

      const mapaRegistrosPorId = new Map<string, Normalizado>();
      registrosNormalizados.forEach((item) => {
        const id = extraerIdDeFila(item.original);
        if (id) {
          mapaRegistrosPorId.set(id, item);
        }
      });
      const mapaMovimientosPorId = new Map<string, Normalizado & { idsucursalSistema: number | null }>();
      movimientosNormalizados.forEach((item) => {
        const id = extraerIdDeFila(item.original);
        if (id) {
          mapaMovimientosPorId.set(id, item);
        }
      });

      const idsSistemaBloqueados = new Set<string>();
      const idsMonchisBloqueados = new Set<string>();
      const registrarBloqueo = (lista: CoincidenciaAutomatica[]) => {
        lista.forEach((item) => {
          if (item.registroId) {
            idsSistemaBloqueados.add(item.registroId);
          }
          if (item.movimientoId) {
            idsMonchisBloqueados.add(item.movimientoId);
          }
        });
      };
      registrarBloqueo(coincidenciasServidor);
      registrarBloqueo(duplicadosServidor);

      const pendientesSistema = registrosNormalizados
        .filter((item) => {
          const id = extraerIdDeFila(item.original);
          if (!id) {
            return true;
          }
          return !idsSistemaBloqueados.has(id);
        })
        .map((item) => item.exportRow);

      const pendientesMonchis = movimientosNormalizados
        .filter((item) => {
          const id = extraerIdDeFila(item.original);
          if (!id) {
            return true;
          }
          return !idsMonchisBloqueados.has(id);
        })
        .map((item) => item.exportRow);

      const coincidenciasFilas = coincidenciasServidor.map((item, index) => {
        const registroNormalizado = item.registroId ? mapaRegistrosPorId.get(item.registroId) : undefined;
        const movimientoNormalizado = item.movimientoId ? mapaMovimientosPorId.get(item.movimientoId) : undefined;
        return {
          orden: index + 1,
          clave: `${item.registroFechaDia ?? ""}|${item.registroIdsucursal ?? ""}|${item.registroMonto ?? ""}`,
          sistema_registro_id: item.registroId ?? "",
          sistema_fecha_dia:
            (registroNormalizado?.exportRow["fecha_dia_local"] as string | undefined) ?? item.registroFechaDia ?? "",
          sistema_idsucursal: item.registroIdsucursal ?? "",
          sistema_monto: item.registroMonto ?? "",
          sistema_forma_pago: item.registroFormaPago ?? "",
          monchis_movimiento_id: item.movimientoId ?? "",
          monchis_fecha_hora: item.movimientoFechaHora ?? "",
          monchis_idsucursal_sistema:
            typeof movimientoNormalizado?.idsucursalSistema === "number" ? movimientoNormalizado.idsucursalSistema : "",
          monchis_monto_con_delivery:
            item.movimientoImporteConDelivery ?? item.movimientoImporteOrden ?? "",
          monchis_tipo_pago: item.movimientoTipoPago ?? "",
        };
      });
      const duplicadosFilas = duplicadosServidor.map((item, index) => {
        const registroNormalizado = item.registroId ? mapaRegistrosPorId.get(item.registroId) : undefined;
        const movimientoNormalizado = item.movimientoId ? mapaMovimientosPorId.get(item.movimientoId) : undefined;
        return {
          orden: index + 1,
          clave: `${item.registroFechaDia ?? ""}|${item.registroIdsucursal ?? ""}|${item.registroMonto ?? ""}`,
          sistema_registro_id: item.registroId ?? "",
          sistema_fecha_dia:
            (registroNormalizado?.exportRow["fecha_dia_local"] as string | undefined) ?? item.registroFechaDia ?? "",
          sistema_idsucursal: item.registroIdsucursal ?? "",
          sistema_monto: item.registroMonto ?? "",
          sistema_forma_pago: item.registroFormaPago ?? "",
          monchis_movimiento_id: item.movimientoId ?? "",
          monchis_fecha_hora: item.movimientoFechaHora ?? "",
          monchis_idsucursal_sistema:
            typeof movimientoNormalizado?.idsucursalSistema === "number" ? movimientoNormalizado.idsucursalSistema : "",
          monchis_monto_con_delivery:
            item.movimientoImporteConDelivery ?? item.movimientoImporteOrden ?? "",
          monchis_tipo_pago: item.movimientoTipoPago ?? "",
        };
      });

      const libro = XLSX.utils.book_new();
      const hojaSistema = XLSX.utils.json_to_sheet(
        registrosNormalizados.length ? registrosNormalizados.map((item) => item.exportRow) : [{ detalle: "Sin registros" }],
      );
      const hojaMonchis = XLSX.utils.json_to_sheet(
        movimientosNormalizados.length ? movimientosNormalizados.map((item) => item.exportRow) : [{ detalle: "Sin registros" }],
      );
      const hojaCoincidencias = XLSX.utils.json_to_sheet(
        coincidenciasFilas.length ? coincidenciasFilas : [{ detalle: "Sin coincidencias" }],
      );
      const hojaPendSistema = XLSX.utils.json_to_sheet(
        pendientesSistema.length ? pendientesSistema : [{ detalle: "Sin pendientes" }],
      );
      const hojaPendMonchis = XLSX.utils.json_to_sheet(
        pendientesMonchis.length ? pendientesMonchis : [{ detalle: "Sin pendientes" }],
      );
      const hojaDuplicados = XLSX.utils.json_to_sheet(
        duplicadosFilas.length ? duplicadosFilas : [{ detalle: "Sin sugeridos" }],
      );
      XLSX.utils.book_append_sheet(libro, hojaSistema, "Base Sistema");
      XLSX.utils.book_append_sheet(libro, hojaMonchis, "Base Monchis");
      XLSX.utils.book_append_sheet(libro, hojaCoincidencias, "Coincidencias");
      XLSX.utils.book_append_sheet(libro, hojaPendSistema, "Pendientes Sistema");
      XLSX.utils.book_append_sheet(libro, hojaPendMonchis, "Pendientes Monchis");
      XLSX.utils.book_append_sheet(libro, hojaDuplicados, "Sugeridos duplicados");
      const buffer = XLSX.write(libro, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const enlace = document.createElement("a");
      enlace.href = URL.createObjectURL(blob);
      enlace.download = `bases-conciliacion-monchis_${autoFechaDesde}_al_${autoFechaHasta}.xlsx`;
      enlace.click();
      URL.revokeObjectURL(enlace.href);
    } catch (error) {
      const mensaje =
        error instanceof Error ? error.message : "No pudimos generar la exportación solicitada.";
      setAutoError(mensaje);
    } finally {
      setAutoExportando(false);
    }
  };

  useEffect(() => {
    setAutoFoundPage((prev) => {
      const totalPages = Math.max(
        Math.ceil(autoCoincidenciasBaseFiltradas.length / AUTO_TAB_PAGE_SIZE),
        1,
      );
      return Math.min(prev, totalPages - 1);
    });
  }, [autoCoincidenciasBaseFiltradas.length]);

  useEffect(() => {
    setAutoSuggestedPage((prev) => {
      const totalPages = Math.max(
        Math.ceil(autoSugeridosFiltrados.length / AUTO_TAB_PAGE_SIZE),
        1,
      );
      return Math.min(prev, totalPages - 1);
    });
  }, [autoSugeridosFiltrados.length]);

  useEffect(() => {
    setAutoMissingPages((prev) => {
      const sistemaPaginas = Math.max(
        Math.ceil(autoPendientesSistemaFiltrados.length / AUTO_TAB_PAGE_SIZE),
        1,
      );
      const monchisPaginas = Math.max(
        Math.ceil(autoPendientesMonchisFiltrados.length / AUTO_TAB_PAGE_SIZE),
        1,
      );
      return {
        sistema: Math.min(prev.sistema, sistemaPaginas - 1),
        monchis: Math.min(prev.monchis, monchisPaginas - 1),
      };
    });
  }, [autoPendientesSistemaFiltrados.length, autoPendientesMonchisFiltrados.length]);

  useEffect(() => {
    setAutoMissingPages((prev) => ({
      ...prev,
      [autoMissingTab]: 0,
    }));
  }, [autoMissingTab]);

  return (
    <>
      <Button color="warning" outline onClick={toggleAutoModal}>
        <i className="ri-magic-line me-1" />
        Conciliación automática
      </Button>
      <Modal isOpen={autoModalVisible} toggle={toggleAutoModal} size="xl">
        <ModalHeader toggle={toggleAutoModal}>Conciliación automática</ModalHeader>
        <ModalBody>
          <MonchisAutoConciliacionFilters
            rangoFechas={autoRangoFechas}
            onRangoCambio={manejarCambioRangoFechas}
            sucursalFiltro={autoSucursalFiltro}
            sucursalOpciones={autoSucursalesSistema}
            onSucursalCambio={(valor) => setAutoSucursalFiltro(valor)}
            onBuscar={() => buscarConciliacionAutomatica()}
            onGuardar={iniciarConciliacion}
            onDescargar={descargarBasesExcel}
            autoCargando={autoCargando}
            autoConciliando={autoConciliando}
            autoExportando={autoExportando}
            puedeGuardar={puedeGuardar}
            controlesDeshabilitados={controlesDeshabilitados}
          />

          <MonchisAutoConciliacionTabs
            autoBasesCargando={autoBasesCargando}
            totals={{
              sistema: totalSistemaVisible,
              monchis: totalMonchisVisible,
              coincidencias: autoCoincidenciasBaseFiltradas.length,
              pendientesSistema: pendientesSistemaVisibles,
              pendientesMonchis: pendientesMonchisVisibles,
              sugeridos: sugeridosDuplicadosVisibles,
            }}
            autoError={autoError}
            autoConciliarMensaje={autoConciliarMensaje}
            onCerrarMensaje={() => setAutoConciliarMensaje(null)}
            autoTab={autoTab}
            setAutoTab={setAutoTab}
            autoResumen={autoResumen}
            autoCoincidenciasBaseFiltradas={autoCoincidenciasBaseFiltradas}
            autoCoincidenciasBaseLength={autoCoincidenciasBase.length}
            autoCoincidenciasPendientesFiltradas={autoCoincidenciasPendientesFiltradas}
            autoCoincidenciasPendientesSet={autoCoincidenciasPendientesSet}
            autoBuscar={autoBuscar}
            setAutoBuscar={setAutoBuscar}
            autoSugeridosDuplicadosLength={sugeridosDuplicadosVisibles}
            autoSugeridosFiltrados={autoSugeridosFiltrados}
            autoCargando={autoCargando}
            autoMissingTab={autoMissingTab}
            setAutoMissingTab={setAutoMissingTab}
            autoPendientesSistemaFiltrados={autoPendientesSistemaFiltrados}
            autoPendientesMonchisFiltrados={autoPendientesMonchisFiltrados}
            totalSinCoincidencias={totalSinCoincidencias}
            onConciliarManual={conciliarCoincidenciaManual}
            onRehacerManual={solicitarRehacerConciliacionManual}
            manualProcesandoId={manualConciliandoId}
            manualConciliadas={manualConciliadas}
            puedeRehacerManual={puedeRehacerConciliacionManual}
            actualizandoEstado={autoActualizandoEstado}
            controlesDeshabilitados={controlesDeshabilitados}
            foundPagination={{
              page: autoFoundPage,
              pageSize: AUTO_TAB_PAGE_SIZE,
              total: autoCoincidenciasBaseFiltradas.length,
              onPageChange: setAutoFoundPage,
            }}
            suggestedPagination={{
              page: autoSuggestedPage,
              pageSize: AUTO_TAB_PAGE_SIZE,
              total: autoSugeridosFiltrados.length,
              onPageChange: setAutoSuggestedPage,
            }}
            missingPagination={{
              sistema: {
                page: autoMissingPages.sistema,
                pageSize: AUTO_TAB_PAGE_SIZE,
                total: autoPendientesSistemaFiltrados.length,
                onPageChange: (page) =>
                  setAutoMissingPages((prev) => ({
                    ...prev,
                    sistema: page,
                  })),
              },
              monchis: {
                page: autoMissingPages.monchis,
                pageSize: AUTO_TAB_PAGE_SIZE,
                total: autoPendientesMonchisFiltrados.length,
                onPageChange: (page) =>
                  setAutoMissingPages((prev) => ({
                    ...prev,
                    monchis: page,
                  })),
              },
            }}
          />
        </ModalBody>
      </Modal>

      <Modal isOpen={Boolean(manualRehacerPendiente)} toggle={cancelarRehacerConciliacionManual}>
        <ModalHeader toggle={cancelarRehacerConciliacionManual}>
          Rehacer conciliación manual
        </ModalHeader>
        <ModalBody>
          Esta coincidencia ya fue conciliada. Si continuás, liberaremos la relación actual para que puedas
          conciliarla y sincronizarla nuevamente desde los registros disponibles.
          <div className="text-muted small mt-2">
            Esta acción sincronizará los registros entre el sistema de gestión y Monchis y registrará el
            cambio en el historial.
          </div>
          <div className="mt-3">
            <div className="text-muted small">
              Registro #{manualRehacerPendiente?.registroNumeroMovimiento ?? manualRehacerPendiente?.registroId}
            </div>
            <div className="text-muted small">
              Movimiento #{manualRehacerPendiente?.movimientoNroOrden ?? manualRehacerPendiente?.movimientoId}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={cancelarRehacerConciliacionManual}>
            Cancelar
          </Button>
          <Button
            color="warning"
            onClick={confirmarRehacerConciliacionManual}
            disabled={
              !manualRehacerPendiente ||
              controlesDeshabilitados ||
              manualConciliandoId ===
                `${manualRehacerPendiente?.registroId ?? ""}-${manualRehacerPendiente?.movimientoId ?? ""}`
            }
          >
            {manualConciliandoId &&
            manualRehacerPendiente &&
            manualConciliandoId ===
              `${manualRehacerPendiente.registroId}-${manualRehacerPendiente.movimientoId}` ? (
              <>
                <Spinner size="sm" className="me-2" /> Liberando…
              </>
            ) : (
              <>
                <i className="ri-arrow-go-back-line me-2" />
                Liberar coincidencia
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default MonchisAutoConciliacion;
